import React from 'react'
import { StripeCardElement } from '@stripe/stripe-js'
import { useStripe, useElements as useStripeElements, CardElement } from '@stripe/react-stripe-js'
import { DEVICE, pxToRem } from 'theme'
import {
  useAuth,
  useFinishTestSessionPayment,
  useCardForm,
  CardFieldName,
  useSetupTestSessionPayment,
} from '@modmd/data'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useToaster } from 'utils/useToaster'
import { Box } from 'components/Layout'
import Button from 'components/Button'
import { Dialog } from 'components/Dialog'
import Card from 'components/Card'
import { Payment } from 'sharedComponents/Payment'

interface Props {
  testSessionId: string
  onDismiss?: () => void
  onSuccess?: () => void
}

export const SessionPaymentDialog: React.VFC<Props> = ({ testSessionId, onDismiss, onSuccess }) => {
  const userAuth = useAuth()
  const stripe = useStripe()
  const elements = useStripeElements()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const { setToastMessage } = useToaster()
  const [paymentSecret, setPaymentSecret] = React.useState<string | undefined>()
  const [setupTestSessionPayment] = useSetupTestSessionPayment()
  const [finishTestSessionPayment] = useFinishTestSessionPayment()
  React.useEffect(() => {
    const setupPaymentSecret = async () => {
      const response = await setupTestSessionPayment({
        variables: { id: testSessionId },
      })
      setPaymentSecret(response.data?.setupTestSessionPayment)
    }
    void setupPaymentSecret()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const cardForm = useCardForm({
    onSubmit: async (values) => {
      if (!stripe || !elements) {
        return false
      }
      try {
        let paymentMethod
        const selectedCard = values[CardFieldName.SELECTED_CARD]
        if (selectedCard) {
          paymentMethod = selectedCard.stripeId
        } else {
          paymentMethod = {
            card: elements.getElement(CardElement) as StripeCardElement,
            billing_details: {
              name: `${userAuth.data.User.firstName || ''} ${userAuth.data.User.firstName || ''}`,
            },
          }
        }
        const stripeResult = await stripe.confirmCardPayment(paymentSecret!, {
          payment_method: paymentMethod,
          setup_future_usage:
            !selectedCard && values[CardFieldName.IS_SAVE_CARD] ? 'off_session' : undefined,
        })
        if (stripeResult.error) {
          setToastMessage(stripeResult.error.message as string, 'danger')
        } else {
          const stripePaymentId = stripeResult?.paymentIntent?.id
          const data = await finishTestSessionPayment({ variables: { stripePaymentId } })
          return !!data
        }
      } catch (error) {
        // ignore error
      }
      return false
    },
  })
  const handleDialogDismiss = React.useCallback(() => {
    cardForm.resetForm()
    if (onDismiss) {
      onDismiss()
    }
  }, [cardForm, onDismiss])
  return (
    <Dialog
      variant={isMobile ? 'fullscreen' : 'center'}
      maxWidth={pxToRem(560)}
      isOpen
      onDismiss={handleDialogDismiss}
    >
      <Box flexDirection="column" width="100%">
        <Card.Title onDismiss={handleDialogDismiss}>Session payment</Card.Title>
        <Card.Content>
          <Payment cardForm={cardForm} />
        </Card.Content>
        <Card.Actions>
          <Button appearance="outlined" mr="1rem" onClick={handleDialogDismiss}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              const isSuccessful = await cardForm.submitForm()
              if (isSuccessful && onSuccess) {
                onSuccess()
              }
            }}
            disabled={!paymentSecret}
            isFetching={cardForm.isSubmitting}
          >
            Pay
          </Button>
        </Card.Actions>
      </Box>
    </Dialog>
  )
}
